// League tables for the all-venues overview, ported from radar's
// features/platforms/league.ts and reshaped for Panther's boards: every venue
// ranked by tokenized value, the same value split by the chain it sits on, and
// by the stock underneath it. Pure functions over boards already fetched, so
// nothing here reads the network.
//
// Withholding follows the boards: a token without a market cap adds nothing to
// a value, a venue whose board did not load is listed as unavailable and left
// out of every total, and a sum with nothing measured is null, never 0.

import { cleanFlow, type FlowMethod } from "./flows";
import { partialFigure, type PartialFigure } from "./partial";
import { chainLabel as chainName, explorerUrl, type Board, type BoardToken, type Coverage, type CoverageMetric } from "./api";
import type { HeatSource } from "./components/heatmap-layout";
import type { StockRow } from "./components/StocksTable";
import { tokenName } from "./format";

/** Where one venue's board stands in the overview. */
export type VenueStatus = "loading" | "ready" | "pending" | "error";

export interface VenueLoad {
  id: string;
  label: string;
  status: VenueStatus;
  /** The last board that loaded; kept through a failed refresh. */
  board: Board | null;
  error?: string;
}

/** Venues whose board is in hand and counts toward totals. */
export function loaded(venues: readonly VenueLoad[]): Array<VenueLoad & { board: Board }> {
  return venues.flatMap((venue) => (venue.board ? [{ ...venue, board: venue.board }] : []));
}

const sumOrNull = (values: ReadonlyArray<number | null>): number | null => {
  const known = values.filter((value): value is number => value !== null && Number.isFinite(value));
  return known.length ? known.reduce((total, value) => total + value, 0) : null;
};

// ---- venue league ----

export interface VenueLeagueRow {
  id: string;
  label: string;
  status: VenueStatus;
  /** Null while the venue is unavailable. */
  tokens: number | null;
  priced: number | null;
  valueUsd: number | null;
  share: number | null;
  mintBurnUsd: number | null;
  /** Net flow over 24h (C-6c): the total, else the tokens' partial sum, else why not. */
  flowFigure: PartialFigure;
  flowMethod: FlowMethod | null;
  /** How much of the venue's value its mint + burn total measures (D-2). */
  mintBurnCoverage?: Coverage;
  /** Chain labels carrying value, largest first. */
  chains: string[];
}

const statusReason = (status: VenueStatus): string => (status === "loading" ? "loading" : status === "pending" ? "first collection in progress" : "board unavailable");

/** A venue's 24h net flow: the published total, else the tokens' partial sum (≥ half the value). */
export function venueFlow(board: Pick<Board, "totals" | "tokens">): PartialFigure {
  return partialFigure(
    cleanFlow(board.totals.net_flow_1d_usd),
    board.tokens.map((t) => ({ value: cleanFlow(t.net_flow_1d_usd), cap: t.market_cap, chain: t.chain })),
    "flows",
  );
}

export function venueLeague(venues: readonly VenueLoad[]): VenueLeagueRow[] {
  const rows: VenueLeagueRow[] = venues.map((venue) => {
    const board = venue.board;
    if (!board) {
      return {
        id: venue.id,
        label: venue.label,
        status: venue.status,
        tokens: null,
        priced: null,
        valueUsd: null,
        share: null,
        mintBurnUsd: null,
        flowFigure: { kind: "none", reason: statusReason(venue.status) },
        flowMethod: null,
        chains: [],
      };
    }
    return {
      id: venue.id,
      label: venue.label,
      status: "ready",
      tokens: board.totals.tokens,
      priced: board.totals.priced,
      valueUsd: board.totals.market_cap,
      share: null,
      mintBurnUsd: board.totals.mint_burn_usd,
      flowFigure: venueFlow(board),
      flowMethod: board.totals.flow_method ?? null,
      mintBurnCoverage: board.totals.coverage?.mint_burn_usd,
      // Largest first; the unsplit bucket reads as "Multi-chain".
      chains: chainSplit(board.tokens)
        .filter((part) => part.valueUsd > 0)
        .sort((a, b) => b.valueUsd - a.valueUsd)
        .map((part) => (part.chain === MULTI_CHAIN ? "Multi-chain" : chainLabel(part.chain))),
    };
  });
  const total = rows.reduce((sum, row) => sum + (row.valueUsd ?? 0), 0);
  return rows
    .map((row) => ({ ...row, share: row.valueUsd !== null && total > 0 ? row.valueUsd / total : null }))
    .sort((a, b) => {
      // Unavailable venues sort last, in picker order.
      if (a.valueUsd === null || b.valueUsd === null) return (a.valueUsd === null ? 1 : 0) - (b.valueUsd === null ? 1 : 0);
      return b.valueUsd - a.valueUsd;
    });
}

// ---- chain league ----

/**
 * The chain league's bucket for value that cannot be placed on one chain: a
 * token whose per-chain supplies add up to more than its circulating supply
 * (xStocks, whose EVM supplies include bridge and inventory copies). Splitting
 * it by supply share would put value on chains that do not hold it.
 */
export const MULTI_CHAIN = "multi";

/**
 * How far per-chain supplies may exceed the token's supply and still be split:
 * 2%. That absorbs reads taken a few blocks apart and rounding, and sits far
 * below the bridged copies it is meant to catch (xStocks run 1.5x and up).
 */
export const SUPPLY_TOLERANCE = 0.02;

export const chainLabel = (chain: string) =>
  chain === MULTI_CHAIN ? "Multi-chain (not split)" : chainName(chain);

export interface ChainValue {
  chain: string;
  valueUsd: number;
  /** Tokens with value on this chain. */
  tokens: number;
}

/**
 * Where a token's market cap sits, by chain. With per-chain supplies, the cap
 * is split by each deployment's share of their summed supply (so the token's
 * value is counted exactly once); without them, all of it sits on `chain`.
 * When those supplies add up to more than the token's own supply by over
 * SUPPLY_TOLERANCE they double count, so the whole cap goes to MULTI_CHAIN.
 * A token without its own supply cannot be checked and is split as reported.
 */
export function tokenChains(token: BoardToken): ChainValue[] {
  const cap = token.market_cap;
  if (cap === null || !Number.isFinite(cap)) return [];
  const deployments = (token.deployments ?? []).filter((d) => d.supply !== null && d.supply > 0);
  const supply = deployments.reduce((total, d) => total + (d.supply ?? 0), 0);
  if (!deployments.length || !(supply > 0)) return [{ chain: token.chain, valueUsd: cap, tokens: 1 }];
  const circulating = token.supply;
  if (circulating !== null && circulating > 0 && supply > circulating * (1 + SUPPLY_TOLERANCE)) {
    return [{ chain: MULTI_CHAIN, valueUsd: cap, tokens: 1 }];
  }
  const byChain = new Map<string, number>();
  for (const d of deployments) byChain.set(d.chain, (byChain.get(d.chain) ?? 0) + (cap * (d.supply ?? 0)) / supply);
  return [...byChain].map(([chain, valueUsd]) => ({ chain, valueUsd, tokens: 1 }));
}

/** A venue's value by chain. */
export function chainSplit(tokens: readonly BoardToken[]): ChainValue[] {
  const byChain = new Map<string, ChainValue>();
  for (const token of tokens) {
    for (const part of tokenChains(token)) {
      const entry = byChain.get(part.chain) ?? { chain: part.chain, valueUsd: 0, tokens: 0 };
      entry.valueUsd += part.valueUsd;
      entry.tokens += part.tokens;
      byChain.set(part.chain, entry);
    }
  }
  return [...byChain.values()];
}

export interface ChainLeagueRow {
  chain: string;
  label: string;
  valueUsd: number;
  share: number;
  tokens: number;
  /** Venue labels with value on the chain, largest first. */
  venues: string[];
}

export function chainLeague(venues: readonly VenueLoad[]): ChainLeagueRow[] {
  const byChain = new Map<string, ChainLeagueRow & { byVenue: Map<string, number> }>();
  for (const venue of loaded(venues)) {
    for (const part of chainSplit(venue.board.tokens)) {
      if (part.valueUsd <= 0) continue;
      const entry = byChain.get(part.chain) ?? {
        chain: part.chain,
        label: chainLabel(part.chain),
        valueUsd: 0,
        share: 0,
        tokens: 0,
        venues: [],
        byVenue: new Map<string, number>(),
      };
      entry.valueUsd += part.valueUsd;
      entry.tokens += part.tokens;
      entry.byVenue.set(venue.label, (entry.byVenue.get(venue.label) ?? 0) + part.valueUsd);
      byChain.set(part.chain, entry);
    }
  }
  const rows = [...byChain.values()];
  const total = rows.reduce((sum, row) => sum + row.valueUsd, 0);
  return rows
    .map(({ byVenue, ...row }) => ({
      ...row,
      share: total > 0 ? row.valueUsd / total : 0,
      venues: [...byVenue].sort((a, b) => b[1] - a[1]).map(([label]) => label),
    }))
    // The unsplit bucket is not a chain; it sorts last whatever its size.
    .sort((a, b) => Number(a.chain === MULTI_CHAIN) - Number(b.chain === MULTI_CHAIN) || b.valueUsd - a.valueUsd);
}

// ---- underlying league ----

/** The stock behind a token: its asset id's ticker, else its own symbol. */
export function underlyingKey(token: Pick<BoardToken, "asset_id" | "symbol">): string {
  const asset = token.asset_id?.startsWith("sym:") ? token.asset_id.slice(4) : null;
  return (asset || token.symbol).toUpperCase();
}

export interface UnderlyingLeagueRow {
  ticker: string;
  name: string;
  valueUsd: number;
  share: number;
  /** Venues issuing it, largest first, with each one's value. */
  venues: Array<{ id: string; label: string; valueUsd: number }>;
}

export function underlyingLeague(venues: readonly VenueLoad[]): UnderlyingLeagueRow[] {
  const byTicker = new Map<string, UnderlyingLeagueRow & { nameValue: number }>();
  for (const venue of loaded(venues)) {
    for (const token of venue.board.tokens) {
      const cap = token.market_cap;
      if (cap === null || !Number.isFinite(cap)) continue;
      const ticker = underlyingKey(token);
      const name = tokenName(token.name);
      const entry = byTicker.get(ticker) ?? { ticker, name, nameValue: -1, valueUsd: 0, share: 0, venues: [] };
      entry.valueUsd += cap;
      // The largest issue names the stock; issuers abbreviate differently.
      if (cap > entry.nameValue && name) {
        entry.name = name;
        entry.nameValue = cap;
      }
      const issuer = entry.venues.find((item) => item.id === venue.id);
      if (issuer) issuer.valueUsd += cap;
      else entry.venues.push({ id: venue.id, label: venue.label, valueUsd: cap });
      byTicker.set(ticker, entry);
    }
  }
  const rows = [...byTicker.values()];
  const total = rows.reduce((sum, row) => sum + row.valueUsd, 0);
  return rows
    .map(({ nameValue: _nameValue, ...row }) => ({
      ...row,
      share: total > 0 ? row.valueUsd / total : 0,
      venues: [...row.venues].sort((a, b) => b.valueUsd - a.valueUsd),
    }))
    .sort((a, b) => b.valueUsd - a.valueUsd);
}

// ---- headline ----

export interface OverviewTotals {
  marketCap: number | null;
  tokens: number;
  priced: number;
  /** Venues whose board loaded, out of all venues. */
  venues: number;
  venuesListed: number;
  chains: number;
  mintBurnUsd: number | null;
  /** Venues reporting a mint + burn figure. */
  mintBurnVenues: number;
  /** Net flow over 24h across every venue (C-6c). */
  netFlow: PartialFigure;
}

export function overviewTotals(venues: readonly VenueLoad[]): OverviewTotals {
  const boards = loaded(venues).map((venue) => venue.board);
  const mintBurn = boards.map((board) => board.totals.mint_burn_usd);
  return {
    marketCap: sumOrNull(boards.map((board) => board.totals.market_cap)),
    tokens: boards.reduce((total, board) => total + board.totals.tokens, 0),
    priced: boards.reduce((total, board) => total + board.totals.priced, 0),
    venues: boards.length,
    venuesListed: venues.length,
    chains: chainLeague(venues).filter((row) => row.chain !== MULTI_CHAIN).length,
    mintBurnUsd: sumOrNull(mintBurn),
    mintBurnVenues: mintBurn.filter((value) => value !== null).length,
    // Every venue's own total when all are published, else every token's flow, partially.
    netFlow: partialFigure(
      boards.length && boards.every((b) => cleanFlow(b.totals.net_flow_1d_usd) !== null) ? sumOrNull(boards.map((b) => cleanFlow(b.totals.net_flow_1d_usd))) : null,
      boards.flatMap((b) => b.tokens.map((t) => ({ value: cleanFlow(t.net_flow_1d_usd), cap: t.market_cap, chain: t.chain }))),
      "flows",
    ),
  };
}

/**
 * The "Venues" tile's note. A venue still loading is not unavailable: it is
 * named as loading until its board lands or its request fails.
 */
export function venuesTileNote(venues: readonly VenueLoad[]): string {
  const withData = loaded(venues).length;
  if (withData === venues.length) return "every venue tracked";
  const loading = venues.filter((venue) => !venue.board && venue.status === "loading").length;
  const unavailable = venues.length - withData - loading;
  const parts = [loading ? `${loading} still loading` : null, unavailable ? `${unavailable} unavailable` : null];
  return `of ${venues.length} tracked; ${parts.filter(Boolean).join(", ")}`;
}

// ---- all stocks ----

/** Every token from every loaded venue, as grid rows with a Venue column. */
export function allStockRows(venues: readonly VenueLoad[]): StockRow[] {
  const total = overviewTotals(venues).marketCap;
  return loaded(venues).flatMap((venue) =>
    venue.board.tokens.map((t) => ({
      id: t.id,
      symbol: t.symbol,
      name: tokenName(t.name),
      address: t.address,
      explorerUrl: explorerUrl(t.chain, t.address),
      venue: venue.label,
      chains: (t.chains ?? [t.chain]).map(chainLabel).join(", "),
      halted: t.halted,
      price: t.price,
      priceNote: t.price_note ?? null,
      change: t.change_24h.price,
      mcap: t.market_cap,
      supply: t.supply,
      share: total && t.market_cap !== null ? t.market_cap / total : null,
      volume: t.volume_usd,
      mintBurn: t.mint_burn_usd,
      netFlow: t.net_flow_1d_usd ?? null,
      netFlow7d: t.net_flow_7d_usd ?? null,
      netFlow30d: t.net_flow_30d_usd ?? null,
      flowMethod: t.flow_method ?? null,
    })),
  );
}

// ---- every token, by chain ----

/**
 * The All-stocks list by chain: one row per token per chain it has supply on,
 * never grouped by the underlying stock, so CRCLon, CRCLx, rCRCL and CRCL on
 * Robinhood stay separate. A chain's market cap is the token's cap split by
 * supply share where the per-chain supplies reconcile; where they include
 * bridged or inventory copies (xStocks), supply × price on that chain is an
 * upper bound and is marked as such. Volume, mint + burn and net flow are
 * per token, so they show on one-chain tokens only.
 */
export function allStockChainRows(venues: readonly VenueLoad[]): StockRow[] {
  const total = overviewTotals(venues).marketCap;
  return loaded(venues).flatMap((venue) =>
    venue.board.tokens.flatMap((t): StockRow[] => {
      const base = {
        symbol: t.symbol,
        name: tokenName(t.name),
        venue: venue.label,
        halted: t.halted,
        price: t.price,
        priceNote: t.price_note ?? null,
        change: t.change_24h.price,
      };
      const deployments = (t.deployments ?? []).filter((d) => d.supply !== null && d.supply > 0);
      if (deployments.length <= 1) {
        const d = deployments[0];
        const chain = d?.chain ?? t.chain;
        return [
          {
            ...base,
            id: t.id,
            chainId: chain,
            address: d?.address ?? t.address,
            explorerUrl: explorerUrl(chain, d?.address ?? t.address),
            chains: chainLabel(chain),
            mcap: t.market_cap,
            supply: t.supply,
            share: total && t.market_cap !== null ? t.market_cap / total : null,
            volume: t.volume_usd,
            mintBurn: t.mint_burn_usd,
            netFlow: t.net_flow_1d_usd ?? null,
            netFlow7d: t.net_flow_7d_usd ?? null,
            netFlow30d: t.net_flow_30d_usd ?? null,
            flowMethod: t.flow_method ?? null,
          },
        ];
      }
      const split = new Map(tokenChains(t).map((part) => [part.chain, part.valueUsd]));
      const upper = split.has(MULTI_CHAIN);
      return deployments.map((d) => {
        // A chain can hold no more than the whole token: its supply × price, capped at the token's own cap.
        const gross = t.price !== null ? t.price * (d.supply ?? 0) : null;
        const mcap = upper ? (gross !== null && t.market_cap !== null ? Math.min(gross, t.market_cap) : gross) : (split.get(d.chain) ?? null);
        return {
          ...base,
          id: `${t.id}@${d.chain}`,
          tokenId: t.id,
          chainId: d.chain,
          mcapUpper: upper && mcap !== null,
          tokenCap: upper ? t.market_cap : undefined,
          address: d.address,
          explorerUrl: explorerUrl(d.chain, d.address),
          chains: chainLabel(d.chain),
          mcap,
          supply: d.supply,
          share: !upper && total && mcap !== null ? mcap / total : null,
          volume: null,
          mintBurn: null,
          netFlow: null,
          netFlow7d: null,
          netFlow30d: null,
          flowMethod: null,
        };
      });
    }),
  );
}

export interface ChainStockStats {
  chain: string;
  label: string;
  /** Distinct tokens with supply on the chain. */
  tokens: number;
  /** Value placed on the chain exactly (split by reconciled supply). */
  valueUsd: number;
  /** Tokens on the chain whose chain supplies include bridged copies: their own caps, each once (at most this is on the chain). */
  upperUsd: number;
}

/** Tokens and value per chain over per-chain rows, largest exact value first. */
export function chainStockStats(rows: readonly StockRow[]): ChainStockStats[] {
  const byChain = new Map<string, ChainStockStats & { ids: Set<string> }>();
  for (const row of rows) {
    const chain = row.chainId ?? "";
    const entry = byChain.get(chain) ?? { chain, label: row.chains, tokens: 0, valueUsd: 0, upperUsd: 0, ids: new Set<string>() };
    entry.ids.add(row.tokenId ?? row.id);
    if (row.mcapUpper) {
      if (row.tokenCap != null && Number.isFinite(row.tokenCap)) entry.upperUsd += row.tokenCap;
    } else if (row.mcap !== null && Number.isFinite(row.mcap)) entry.valueUsd += row.mcap;
    byChain.set(chain, entry);
  }
  return [...byChain.values()]
    .map(({ ids, ...stats }) => ({ ...stats, tokens: ids.size }))
    .sort((a, b) => b.valueUsd - a.valueUsd || b.upperUsd - a.upperUsd || b.tokens - a.tokens);
}

/**
 * The list's total, each token once: values placed on a chain, plus the whole
 * cap of each token whose chain split is not known.
 */
export function stockRowsTotal(rows: readonly StockRow[]): { exact: number; unsplit: number; tokens: number } {
  let exact = 0;
  const unsplit = new Map<string, number>();
  const ids = new Set<string>();
  for (const row of rows) {
    const id = row.tokenId ?? row.id;
    ids.add(id);
    if (row.mcapUpper) {
      if (row.tokenCap != null && Number.isFinite(row.tokenCap)) unsplit.set(id, row.tokenCap);
    } else if (row.mcap !== null && Number.isFinite(row.mcap)) exact += row.mcap;
  }
  return { exact, unsplit: [...unsplit.values()].reduce((s, v) => s + v, 0), tokens: ids.size };
}

// ---- heatmap ----

/**
 * Every token from every loaded venue for the market-wide heatmap. Ids are
 * prefixed with the venue (two venues can list the same contract) and each
 * token carries its venue label for the tooltip and inspector.
 */
export function allHeatTokens(venues: readonly VenueLoad[]): HeatSource[] {
  return loaded(venues).flatMap((venue) =>
    venue.board.tokens.map((t) => ({ ...t, id: `${venue.id}/${t.id}`, venue: venue.label })),
  );
}

// ---- coverage ----

/**
 * Share (0–1) of all loaded venues' value that a summed overview figure
 * measures. Only venues whose own total is published add to the sum, so only
 * they count, each by its market cap times its own coverage. Null when no
 * venue has a market cap or a loaded board does not report coverage.
 */
export function overviewCoverage(venues: readonly VenueLoad[], metric: CoverageMetric, total: (board: Board) => number | null): number | null {
  let measured = 0;
  let value = 0;
  for (const { board } of loaded(venues)) {
    const cap = board.totals.market_cap;
    if (cap === null || !Number.isFinite(cap)) continue;
    const coverage = board.totals.coverage?.[metric];
    if (!coverage) return null;
    value += cap;
    if (total(board) !== null) measured += cap * (coverage.measured_cap_share ?? 0);
  }
  return value > 0 ? Math.min(1, measured / value) : null;
}
